import { z } from 'zod';

type FieldTypeMapping = Record<string, 'string' | 'number' | 'boolean' | 'date'>;

const getZodTypeForFieldType = (fieldType?: string) => {
  switch (fieldType) {
    case 'string':
      return z.string();
    case 'number':
      return z.number();
    case 'boolean':
      return z.boolean();
    case 'date':
      return z.coerce.date();
    default:
      return z.union([z.string(), z.number(), z.boolean(), z.date()]);
  }
};

const MAX_IN_ARGS = 30;

export const createBaseFilterSchema = <TFieldTypes extends FieldTypeMapping>(
  fieldTypes: TFieldTypes,
) => {
  const fields = Object.keys(fieldTypes);
  return z
    .object({
      field: z.enum(
        fields as [Extract<keyof TFieldTypes, string>, ...Extract<keyof TFieldTypes, string>[]],
      ),
      operator: z.enum(['eq', 'gt', 'gte', 'lt', 'lte', 'like', 'in', 'isNull']),
      value: z
        .union([
          z.string(),
          z.number(),
          z.boolean(),
          z.date(),
          z.array(z.union([z.string(), z.number(), z.boolean(), z.date()])),
        ])
        .optional(),
    })
    .strict()
    .refine((data) => data.operator === 'isNull' || data.value !== undefined, {
      message: 'Value is required for all operators except isNull',
    })
    .refine((data) => data.operator !== 'isNull' || data.value === undefined, {
      message: 'Value must not be provided when using isNull operator',
    })
    .refine(
      (data) =>
        data.operator !== 'in' || (Array.isArray(data.value) && data.value.length <= MAX_IN_ARGS),
      {
        message: `Value must be an array with maximum ${MAX_IN_ARGS} items when using 'in' operator`,
      },
    )
    .refine((data) => data.operator !== 'like' || typeof data.value === 'string', {
      message: 'Value must be a string when using like operator',
    })
    .transform((data) => {
      if (data.field in fieldTypes && data.operator !== 'isNull' && data.value !== undefined) {
        const fieldType = fieldTypes[data.field];
        const zodType = getZodTypeForFieldType(fieldType);
        if (data.operator === 'in' && Array.isArray(data.value)) {
          const arrayType = z.array(zodType);
          data.value = arrayType.parse(data.value);
        } else if (data.operator !== 'in') {
          data.value = zodType.parse(data.value);
        }
      }

      return data;
    });
};

export const createFilterDTOSchema = <TFieldTypes extends FieldTypeMapping>(
  fieldTypes: TFieldTypes,
) => {
  const baseFilterSchema = createBaseFilterSchema(fieldTypes).optional();
  const filterGroupSchema = z
    .object({
      AND: z.array(baseFilterSchema).optional(),
      OR: z.array(baseFilterSchema).optional(),
      NOT: baseFilterSchema,
    })
    .strict();
  const advancedFilterSchema = z
    .object({
      AND: z.array(z.union([baseFilterSchema, filterGroupSchema])).optional(),
      OR: z.array(z.union([baseFilterSchema, filterGroupSchema])).optional(),
      NOT: z.union([baseFilterSchema, filterGroupSchema]).optional(),
    })
    .strict();
  return {
    filterSchema: z.union([baseFilterSchema, filterGroupSchema, advancedFilterSchema]).optional(),
    baseFilterSchema,
  };
};

export type FilterDTO = z.infer<ReturnType<typeof createFilterDTOSchema>['filterSchema']>;
export type BaseFilterDTO = z.infer<ReturnType<typeof createBaseFilterSchema>>;
